/**
 * Platform-bundled fonts (hosted on Supabase Storage, not Google Fonts).
 * Selectable from the same UI as Google Fonts; loaded via @font-face.
 */

export interface PlatformFontFace {
  weight: number;
  url: string;
  style?: 'normal' | 'italic';
}

export interface PlatformFont {
  name: string;
  category: 'sans-serif' | 'serif' | 'display';
  faces: PlatformFontFace[];
}

const STORAGE_BASE =
  'https://bfzglzurqrqzqnpojxpr.supabase.co/storage/v1/object/public/fonts/platform';

export const PLATFORM_FONTS: PlatformFont[] = [
  {
    name: 'ExpoArabic',
    category: 'sans-serif',
    faces: [
      { weight: 500, url: `${STORAGE_BASE}/expoarabic-medium.woff2` },
      { weight: 700, url: `${STORAGE_BASE}/expoarabic-bold.woff2` },
    ],
  },
];

export const isPlatformFont = (name: string | null | undefined): boolean =>
  !!name && PLATFORM_FONTS.some((f) => f.name === name);

export const getPlatformFont = (name: string): PlatformFont | undefined =>
  PLATFORM_FONTS.find((f) => f.name === name);

/**
 * Inject @font-face declarations for a platform font. Idempotent.
 */
export const injectPlatformFont = (name: string): void => {
  if (typeof document === 'undefined') return;
  const font = getPlatformFont(name);
  if (!font) return;

  const styleId = `platform-font-${name.replace(/\s/g, '-')}`;
  if (document.getElementById(styleId)) return;

  const css = font.faces
    .map(
      (f) => `@font-face {
  font-family: "${font.name}";
  src: url("${f.url}") format("woff2");
  font-weight: ${f.weight};
  font-style: ${f.style ?? 'normal'};
  font-display: swap;
}`
    )
    .join('\n');

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = css;
  document.head.appendChild(style);
};

/** Inject all platform fonts at once (e.g. for the font picker preview). */
export const injectAllPlatformFonts = (): void => {
  PLATFORM_FONTS.forEach((f) => injectPlatformFont(f.name));
};